import { useId, useState } from "react";
import { Plus } from "lucide-react";
import { cx } from "../../lib/cx";

/**
 * One-open-at-a-time accordion. Panels animate their height with a
 * grid-template-rows transition (0fr ↔ 1fr), so opening one smoothly closes
 * the other. Panel text stays in the prerendered HTML.
 */
export default function Accordion({ items, defaultOpen = 0 }) {
  const [open, setOpen] = useState(defaultOpen);
  const id = useId();
  return (
    <div className="divide-y divide-line border-y border-line">
      {items.map(({ title, text }, i) => {
        const isOpen = open === i;
        return (
          <div key={title}>
            <h3>
              <button
                id={`${id}-tab-${i}`}
                aria-expanded={isOpen}
                aria-controls={`${id}-panel-${i}`}
                onClick={() => setOpen(isOpen ? -1 : i)}
                className="group flex w-full items-center justify-between gap-6 py-6 text-left"
              >
                <span
                  className={cx(
                    "text-lg font-bold transition-colors md:text-xl",
                    isOpen
                      ? "text-ink"
                      : "text-slate group-hover:text-blue-deep",
                  )}
                >
                  {title}
                </span>
                <span
                  aria-hidden="true"
                  className={cx(
                    "grid size-10 shrink-0 place-items-center rounded-brand transition-colors duration-300",
                    isOpen
                      ? "bg-yellow text-ink"
                      : "bg-surface text-slate group-hover:bg-yellow-soft",
                  )}
                >
                  <Plus
                    size={20}
                    className={cx(
                      "transition-transform duration-300",
                      isOpen && "rotate-45",
                    )}
                  />
                </span>
              </button>
            </h3>
            <div
              id={`${id}-panel-${i}`}
              role="region"
              aria-labelledby={`${id}-tab-${i}`}
              className={cx(
                "grid transition-[grid-template-rows] duration-300 ease-out",
                isOpen ? "grid-rows-[1fr]" : "grid-rows-[0fr]",
              )}
            >
              <div
                className="overflow-hidden"
                inert={isOpen ? undefined : true}
              >
                <p className="max-w-[68ch] pb-6 md:pr-16 leading-relaxed text-muted">
                  {text}
                </p>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
